import appName from "@mgdx/shared/src/appName";
import appVersion from "@mgdx/shared/src/appVersion";
import type { ErrorEvent, EventHint } from "@sentry/react";
import * as Sentry from "@sentry/react";

const ignoreExceptionValues = [
  "Load failed",
  "ネットワーク接続が切れました。",
  "キャンセルしました",
  "network error on stackdriver report",
  "[auth/network-request-failed] Firebase: Error (auth/network-request-failed).",
  "Firebase: Error (auth/network-request-failed).",
  "[auth/quota-exceeded] Firebase: Error (auth/quota-exceeded).",
  "Firebase: Error (auth/quota-exceeded).",
  "[auth/too-many-requests] Firebase: Error (auth/too-many-requests).",
  "Firebase: Error (auth/too-many-requests).",
  "操作を完了できませんでした。ソフトウェアにより接続が中止されました",
  "Unexpected token '<'",
  "N",
  "Non-Error promise rejection captured with value: Timeout",
  "UnhandledRejection: Non-Error promise rejection captured with value: Timeout",
  "null is not an object (evaluating 'window.localStorage.setItem')",
  "null is not an object (evaluating 'Object.keys(localStorage)')",
  "null is not an object (evaluating 'window.localStorage.getItem')",
  "null is not an object (evaluating 'localStorage.debug=\"\"')",
  "Firebase: Error thrown when reading from storage. Original error: Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.. (app/storage-get).",
  "TimeoutError: Transaction timed out due to inactivity.",
  "操作を完了できませんでした。（kCFErrorDomainCFNetworkエラー303）",
  "Unhandled promise rejection.  TypeError: Load failed",
  "UnknownError: Connection to Indexed Database server lost. Refresh the page to try again",
  "Unhandled promise rejection.  UnknownError: Connection to Indexed Database server lost. Refresh the page to try again",
  "Failed to fetch",
  "Failed to execute 'json' on 'Response': body stream already read",
  "Non-Error promise rejection captured",
  "The object can not be found here.",
  "not found. Not rendering React",
];

const ignoreExceptionPreMatchValues = ["Failed to fetch"];

// fetch が通信断で投げる TypeError の文言(Chrome / Safari / Firefox)
const networkErrorMessages = ["Load failed", "NetworkError when attempting to fetch resource."];

export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError &&
  (networkErrorMessages.includes(error.message) ||
    ignoreExceptionPreMatchValues.some((pre) => error.message.startsWith(pre)));

// 利用者の操作・セッション切れ・業務ルールで起きる想定内のエラー。Stackdriver には送るが Sentry には送らない
// 403 は侵入検知のため対象に含めない
const expectedErrorNames = new Set(["ApiUnauthorizedError", "ApiNotFoundError", "ApiTimeoutError", "NotAllowedError"]);

// detail_code を文言化した API の業務エラーは getApiError が cause 付きで作る。
// アプリ側で投げる ApiCustomError(一括予約ジョブの失敗など)は cause を持たないため送る
const API_CUSTOM_ERROR_NAME = "ApiCustomError";

const expectedErrorMessagePatterns = [
  /not found\. Not rendering React$/,
  /NotAllowedError: Permission (denied|dismissed)/,
  /Requested device not found/,
  /Could not start video source/,
];

const apiErrorNames = new Set([
  "ApiBadRequestError",
  "ApiUnauthorizedError",
  "ApiForbiddenError",
  "ApiNotFoundError",
  "ApiInternalServerError",
  "ApiUnprocessableEntityError",
  "ApiError",
]);

const warningApiStatuses = new Set([400, 403, 409, 422]);

const FORBIDDEN_STATUS = 403;

const sensitiveKeyPattern =
  "request_?token|answer_?token|auth_?token|access_?token|id_?token|refresh_?token|token|oob_?code|continue_?url" +
  "|patient_?name|first_?name|last_?name|email|keyword|insurance_?numbers";

// URL として解釈できない文字列(message など)に含まれるトークン・個人情報のクエリ値をマスクする
// 区切りは ? & # に加え、エンコード済み(%3F %26 %23)と HTML エスケープ(&amp;)も対象にする
// クエリの code は OAuth の認可コードのため対象にする
const sensitiveParamPattern = new RegExp(
  `((?:[?&#;]|&amp;|%3F|%26|%23)(?:${sensitiveKeyPattern}|code)(?:=|%3D))[^&#"'\\s]*`,
  "gi"
);

// JSON を文字列化した message に含まれるトークン・個人情報の値をマスクする。値は文字列・配列・数値などを対象にする
// JSON の code は API のエラーコードのため対象にしない
const sensitiveJsonPattern = new RegExp(
  `"(${sensitiveKeyPattern})"\\s*:\\s*(?:"(?:[^"\\\\]|\\\\.)*"|\\[[^\\]]*\\]|[^,}\\]\\s]+)`,
  "gi"
);

// 英小文字と区切り記号だけの固定パスとバージョン(v1 など)以外は、ID やトークンとみなして :id に置き換える
const literalPathSegmentPattern = /^([a-z][a-z_-]*|v\d+)$/;

const statusUrlMessagePattern = /^\[(\d{3})\] (\S+)/;

type ApiErrorDetail = { status: number; url: string };

export const maskSensitiveText = (text: string) =>
  text.replace(sensitiveParamPattern, "$1[Filtered]").replace(sensitiveJsonPattern, '"$1":"[Filtered]"');

// 相対 URL を解析するための仮の基準。結果にこの origin が出たら相対 URL として origin を付けずに返す
const RELATIVE_URL_BASE = "https://relative.invalid";

// クエリの値をすべて落とし、フラグメントと認証情報も除く。相対 URL(navigation breadcrumb の from / to など)も対象にする
export const sanitizeUrl = (rawUrl: string): string => {
  if (!rawUrl) return rawUrl;

  try {
    const { origin, pathname, searchParams } = new URL(rawUrl, RELATIVE_URL_BASE);
    const query = [...new Set(searchParams.keys())].map((key) => `${encodeURIComponent(key)}=[Filtered]`).join("&");
    const isRelative = origin === RELATIVE_URL_BASE;
    // "?q=..." のようにクエリから始まる場合は、元に無いパスを付けない
    const path = isRelative && /^[?#]/.test(rawUrl) ? "" : `${isRelative ? "" : origin}${pathname}`;

    return query ? `${path}?${query}` : path;
  } catch {
    // URL として解釈できない場合は、値を残さないよう ? と # 以降をすべて落とす
    return rawUrl.split(/[?#]/, 1)[0];
  }
};

// responseToErrorMessage / parseErrorResponse が作る JSON 形式と "[status] url" 形式から status と url を取り出す
export const parseApiErrorDetail = (message: string): ApiErrorDetail | undefined => {
  try {
    const parsed: unknown = JSON.parse(message);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "status" in parsed &&
      "url" in parsed &&
      typeof parsed.status === "number" &&
      typeof parsed.url === "string"
    ) {
      return { status: parsed.status, url: parsed.url };
    }
  } catch {
    // JSON でなければ "[status] url" 形式として扱う
  }

  const matched = statusUrlMessagePattern.exec(message);
  if (!matched) return undefined;

  return { status: Number(matched[1]), url: matched[2] };
};

// ID ごとに Issue が分かれないよう、固定パス以外を :id に置き換え query を除く
export const normalizeApiEndpoint = (url: string): string | undefined => {
  try {
    const { host, pathname } = new URL(url);
    const normalizedPath = pathname
      .split("/")
      .map((segment) => (segment === "" || literalPathSegmentPattern.test(segment) ? segment : ":id"))
      .join("/");

    return `${host}${normalizedPath}`;
  } catch {
    return undefined;
  }
};

const isExpectedErrorMessage = (message: string) =>
  expectedErrorMessagePatterns.some((pattern) => pattern.test(message));

export const isExpectedErrorForSentry = (error: unknown): boolean => {
  if (typeof error === "string") return isExpectedErrorMessage(error);
  // DOMException は環境によって Error を継承しないため、name と message の有無で判定する
  if (typeof error !== "object" || error === null) return false;

  const name = "name" in error && typeof error.name === "string" ? error.name : "";
  const message = "message" in error && typeof error.message === "string" ? error.message : "";
  const hasCause = "cause" in error && error.cause !== undefined;

  return (
    expectedErrorNames.has(name) || (name === API_CUSTOM_ERROR_NAME && hasCause) || isExpectedErrorMessage(message)
  );
};

const isIgnoredExceptionValue = (value: string) =>
  ignoreExceptionValues.includes(value) || ignoreExceptionPreMatchValues.some((pre) => value.startsWith(pre));

const applyApiErrorContext = (event: ErrorEvent, detail: ApiErrorDetail, errorName: string) => {
  const endpoint = normalizeApiEndpoint(detail.url);
  const status = String(detail.status);

  if (endpoint) {
    event.fingerprint = [errorName, status, endpoint];
  }
  event.tags = {
    ...event.tags,
    "api.status": status,
    ...(endpoint ? { "api.endpoint": endpoint } : {}),
    ...(detail.status === FORBIDDEN_STATUS ? { "security.forbidden": "true" } : {}),
  };
  event.extra = { ...event.extra, "api.url": sanitizeUrl(detail.url) };

  if (warningApiStatuses.has(detail.status)) {
    event.level = "warning";
  }
};

const breadcrumbUrlKeys = ["url", "from", "to"];

const maskSensitiveEventData = (event: ErrorEvent) => {
  // setUser に氏名やメールを入れても送らないよう、ユーザーは ID だけに絞る
  if (event.user) event.user = event.user.id === undefined ? undefined : { id: event.user.id };

  if (event.message) event.message = maskSensitiveText(event.message);

  if (event.request) {
    if (event.request.url) event.request.url = sanitizeUrl(event.request.url);
    if (event.request.headers?.Referer) event.request.headers.Referer = sanitizeUrl(event.request.headers.Referer);
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) exception.value = maskSensitiveText(exception.value);
  });

  if (event.extra) {
    for (const [key, value] of Object.entries(event.extra)) {
      if (typeof value === "string") event.extra[key] = maskSensitiveText(value);
    }
  }

  event.breadcrumbs?.forEach((breadcrumb) => {
    if (breadcrumb.message) breadcrumb.message = maskSensitiveText(breadcrumb.message);

    const data = breadcrumb.data;
    if (!data) return;
    for (const key of breadcrumbUrlKeys) {
      const value = data[key];
      if (typeof value === "string") data[key] = sanitizeUrl(value);
    }
  });
};

export const processSentryEvent = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const exceptions = event.exception?.values ?? [];

  if (exceptions.some(({ value }) => value && (isIgnoredExceptionValue(value) || isExpectedErrorMessage(value)))) {
    return null;
  }

  // 連鎖した例外は大元の例外が末尾に入る
  const mainException = exceptions.at(-1);
  const mainExceptionType = mainException?.type ?? "";
  // originalException が無い場合は、連鎖した例外(cause)の有無で API 由来の業務エラーかを判断する
  const isApiCustomErrorWithCause = mainExceptionType === API_CUSTOM_ERROR_NAME && exceptions.length > 1;
  if (
    isExpectedErrorForSentry(hint.originalException) ||
    expectedErrorNames.has(mainExceptionType) ||
    isApiCustomErrorWithCause
  ) {
    return null;
  }

  if (mainException?.type && mainException.value && apiErrorNames.has(mainException.type)) {
    const detail = parseApiErrorDetail(mainException.value);
    if (detail) applyApiErrorContext(event, detail, mainException.type);
  }

  maskSensitiveEventData(event);

  return event;
};

export const getSentryDsn = () => process.env.NEXT_PUBLIC_SENTRY_DSN ?? process.env.SENTRY_DSN;

export const getBuildEnv = () => process.env.NEXT_PUBLIC_BUILD_ENV ?? process.env.BUILD_ENV ?? "dev";

export const isSentryEnabled = () => (process.env.NEXT_PUBLIC_ENABLE_SENTRY ?? process.env.ENABLE_SENTRY) === "true";

export function initSentry({ dsn }: Pick<Sentry.BrowserOptions, "dsn">) {
  if (!dsn) return;
  const buildEnv = getBuildEnv();
  const isProduction = buildEnv === "prd";

  Sentry.init({
    dsn,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: isProduction ? 0.001 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    environment: buildEnv,
    release: `${appName}.${buildEnv}.${appVersion}`,
    debug: !isProduction,
    beforeSend: processSentryEvent,
  });
}
